import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import promotionService from '../../../services/promotionService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { buildToggleAction, useCanUpdate } from '../../common/activeToggle';
import { useDataTable } from '../../../hooks/useDataTable';
import { useCurrency } from '../../../hooks/useCurrency';

const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '-');

const formatType = (type, t) => {
    if (type === 'PERCENTAGE') return '%';
    if (type === 'FIXED_AMOUNT') return 'Fixe';
    return t(`promotions.types.${type}`, type);
};

const PromotionsList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { format } = useCurrency();
    const canToggle = useCanUpdate('promotions.update');
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Page, size, search, sort and the active filter live in the URL (/promotions?page=0&size=20&...).
    const table = useDataTable(promotionService.getPromotionsPage, {}, {
        onError: () => toast.error(t('promotions.fetchError')),
    });

    const handleToggleActive = async (promotion) => {
        try {
            await promotionService.toggleActive(promotion.id);
            toast.success(promotion.isActive === false ? t('common.activated') : t('common.deactivated'));
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.toggleError'));
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await promotionService.deletePromotion(selectedPromotion.id);
            toast.success(t('promotions.deleteSuccess'));
            setDeleteDialogOpen(false);
            setSelectedPromotion(null);
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.deleteError'));
        }
    };

    const valueLabel = (promotion) =>
        promotion.type === 'PERCENTAGE' ? `${promotion.value}%` : format(promotion.value);

    const columns = [
        { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 180 },
        {
            field: 'storeName',
            headerName: t('roles.store'),
            width: 160,
            cellRenderer: (params) =>
                params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-',
        },
        {
            field: 'code',
            headerName: t('promotions.code'),
            width: 140,
            cellRenderer: (params) =>
                params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-',
        },
        {
            field: 'type',
            headerName: t('promotions.type'),
            width: 120,
            cellRenderer: (params) => (
                <Chip
                    label={formatType(params.value, t)}
                    size="small"
                    color={params.value === 'PERCENTAGE' ? 'primary' : 'secondary'}
                />
            ),
            tooltipValueGetter: (params) => formatType(params.value, t),
        },
        {
            field: 'value',
            headerName: t('promotions.value'),
            width: 130,
            cellRenderer: (params) => valueLabel(params.data),
            tooltipValueGetter: (params) => valueLabel(params.data),
        },
        {
            field: 'usageCount',
            headerName: t('promotions.usageCount'),
            width: 130,
            cellRenderer: (params) =>
                `${params.value ?? 0}${params.data.usageLimit ? ` / ${params.data.usageLimit}` : ''}`,
        },
        {
            field: 'startDate',
            headerName: t('promotions.startDate'),
            width: 130,
            cellRenderer: (params) => formatDate(params.value),
            tooltipValueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'endDate',
            headerName: t('promotions.endDate'),
            width: 130,
            cellRenderer: (params) => formatDate(params.value),
            tooltipValueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'isActive',
            headerName: t('common.status'),
            width: 130,
            cellRenderer: (params) => (
                <Chip
                    icon={params.value !== false ? <ActiveIcon /> : <InactiveIcon />}
                    label={params.value !== false ? t('common.active') : t('common.inactive')}
                    color={params.value !== false ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.details'),
                            color: 'info',
                            onClick: () => navigate(`/promotions/view/${params.data.id}`),
                        },
                        {
                            key: 'edit',
                            icon: EditIcon,
                            title: t('common.edit'),
                            color: 'primary',
                            onClick: () => navigate(`/promotions/edit/${params.data.id}`),
                        },
                        buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
                        {
                            key: 'delete',
                            icon: DeleteIcon,
                            title: t('common.delete'),
                            color: 'error',
                            onClick: () => {
                                setSelectedPromotion(params.data);
                                setDeleteDialogOpen(true);
                            },
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('promotions.title')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/promotions/create')}
                >
                    {t('promotions.createPromotion')}
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('promotions.searchPlaceholder')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <ActiveFilter
                        value={table.params.active || 'all'}
                        onChange={(active) => table.updateParams({ active })}
                        sx={{ mb: 2 }}
                    />

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                    />
                </CardContent>
            </Card>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>{t('promotions.deletePromotion')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('common.deleteConfirmText', { name: selectedPromotion?.name })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PromotionsList;
